/**
 * GameScene — Main gameplay Phaser Scene for the Adventure World Explorer.
 *
 * Responsibilities:
 * 1. Render the overworld as a side-scrolling 2D scene with parallax background layers
 * 2. Configure Arcade Physics with gravity 600 (forgiving for children 4-6)
 * 3. Implement camera follow with smooth interpolation (lerp 0.1)
 * 4. Manage layer depth ordering for visual consistency
 * 5. Handle responsive viewport (base 1280×720, scale 320px-2560px)
 * 6. Adapt to orientation changes within 300ms
 * 7. Place challenge triggers and wire challenge overlay integration
 * 8. Music and sounds
 * 9. Touch controls for mobile
 * 10. Enemies in zones 2-5
 * 11. Power-ups
 * 12. Progress persistence
 *
 * Scene key: 'GameScene'
 * Receives: { mapId: string } from MapSelectScene
 */

import Phaser from 'phaser';
import { generateLevel, type ProceduralLevelResult } from './ProceduralLevel';
import { ChallengeOverlayBridge } from '../systems/ChallengeOverlayBridge';
import { IframeMotorFactory } from '../systems/EduAdventureMotorAdapter';
import { GameStateCoordinator } from '../systems/GameStateCoordinator';
import { CollectibleSystem } from '../systems/CollectibleSystem';
import { CollectiblesHUD } from '../systems/CollectiblesHUD';
import { FinishTriggerSystem } from '../systems/FinishTriggerSystem';
import { Collectible, type CollectibleConfig } from '../entities/Collectible';
import { getCollectiblePlacements } from '../config/collectible-placements';

// ─── Layer Depth Constants ───────────────────────────────────────────────────

export const LAYER_DEPTHS = {
  BACKGROUND_SKY: 0,
  BACKGROUND_MOUNTAINS: 1,
  BACKGROUND_TREES: 2,
  TILEMAP: 10,
  HAZARDS: 15,
  POWER_UPS: 18,
  ENEMIES: 20,
  TRIGGERS: 22,
  PLAYER: 25,
  HUD: 100,
} as const;

export type LayerDepthKey = keyof typeof LAYER_DEPTHS;

// ─── Viewport Constants ──────────────────────────────────────────────────────

export const BASE_WIDTH = 1280;
export const BASE_HEIGHT = 720;
export const SAFE_AREA_MARGIN = 16;
export const MIN_VIEWPORT_WIDTH = 320;
export const MAX_VIEWPORT_WIDTH = 2560;
export const ORIENTATION_ADAPT_MS = 300;

// ─── Physics Constants ───────────────────────────────────────────────────────

export const GRAVITY_Y = 600;
const GROUND_Y = BASE_HEIGHT - 64;
const TILE_SIZE = 64;

// ─── Camera Constants ────────────────────────────────────────────────────────

export const CAMERA_LERP = 0.1;

// ─── Parallax Configuration ─────────────────────────────────────────────────

export const PARALLAX_SCROLL_FACTORS = {
  sky: 0.1,
  mountains: 0.3,
  trees: 0.6,
} as const;

// ─── Scene Data Interface ────────────────────────────────────────────────────

export interface GameSceneData {
  mapId: string;
}

// ─── Parallax Layer Info ─────────────────────────────────────────────────────

interface ParallaxLayerInfo {
  image: Phaser.GameObjects.Image;
  scrollFactor: number;
  depth: number;
}

// ─── Trigger Info ────────────────────────────────────────────────────────────

interface TriggerInfo {
  sprite: Phaser.GameObjects.Image;
  caveId: string;
  x: number;
  completed: boolean;
  promptText: Phaser.GameObjects.Text | null;
  challengeData: object;
}

// ─── Enemy Info ──────────────────────────────────────────────────────────────

interface EnemyInfo {
  sprite: Phaser.GameObjects.Image;
  startX: number;
  direction: number;
  range: number;
  speed: number;
  alive: boolean;
}

// ─── Power-Up Info ───────────────────────────────────────────────────────────

interface PowerUpInfo {
  sprite: Phaser.GameObjects.Image;
  type: 'star' | 'fireball' | 'shield' | 'spring';
  startY: number;
  collected: boolean;
  floatTime: number;
}

// ─── Progress Persistence Key ────────────────────────────────────────────────

const PROGRESS_KEY = 'awe_progress_default';

// ─── Trigger Definitions ─────────────────────────────────────────────────────

/** Returns the trigger texture key based on mapId, falling back to generic triggers */
function getTriggerKey(mapId: string, index: number): string {
  const perWorldTriggers: Record<string, string> = {
    'floresta-encantada': 'floresta-caverna',
    'montanha-gelada': 'montanha-caverna',
    'reino-encantado': 'reino-caverna',
    'espaco-sideral': 'espaco-caverna',
  };
  const worldTrigger = perWorldTriggers[mapId];
  if (worldTrigger) return worldTrigger;

  // Fallback to legacy triggers
  const legacyKeys = ['trigger-cave', 'trigger-portal', 'trigger-tunnel', 'trigger-magic-tree', 'trigger-crystal'];
  return legacyKeys[index] ?? 'trigger-cave';
}

/** Trigger X positions per world — placed exactly on ground platforms */
const TRIGGER_POSITIONS_MAP: Record<string, Array<{ x: number; caveId: string }>> = {
  'floresta-encantada': [
    { x: 2500, caveId: 'cave-1' },  // Zone 1 continuous ground
    { x: 7300, caveId: 'cave-2' },  // Zone 2 platform at s+2300 = 7300
    { x: 12800, caveId: 'cave-3' }, // Zone 3 platform at s+2800 = 12800
    { x: 17700, caveId: 'cave-4' }, // Zone 4 platform at s+2700 = 17700
    { x: 22600, caveId: 'cave-5' }, // Zone 5 platform at s+2600 = 22600
  ],
  'montanha-gelada': [
    { x: 3700, caveId: 'cave-1' },  // Zone 1 platform at s+3700 = 3700
    { x: 8750, caveId: 'cave-2' },  // Zone 2 platform at s+3750 = 8750
    { x: 12950, caveId: 'cave-3' }, // Zone 3 platform at s+2950 = 12950
    { x: 17850, caveId: 'cave-4' }, // Zone 4 platform at s+2850 = 17850
    { x: 22700, caveId: 'cave-5' }, // Zone 5 platform at s+2700 = 22700
  ],
  'reino-encantado': [
    { x: 3600, caveId: 'cave-1' },  // Zone 1 platform at s+3600 = 3600
    { x: 7900, caveId: 'cave-2' },  // Zone 2 platform at s+2900 = 7900
    { x: 12850, caveId: 'cave-3' }, // Zone 3 platform at s+2850 = 12850
    { x: 17750, caveId: 'cave-4' }, // Zone 4 platform at s+2750 = 17750
    { x: 22650, caveId: 'cave-5' }, // Zone 5 platform at s+2650 = 22650
  ],
  'espaco-sideral': [
    { x: 3200, caveId: 'cave-1' },  // Zone 1 platform at s+3200 = 3200
    { x: 7550, caveId: 'cave-2' },  // Zone 2 platform at s+2550 = 7550
    { x: 12450, caveId: 'cave-3' }, // Zone 3 platform at s+2450 = 12450
    { x: 17350, caveId: 'cave-4' }, // Zone 4 platform at s+2350 = 17350
    { x: 22200, caveId: 'cave-5' }, // Zone 5 platform at s+2200 = 22200
  ],
};

const TRIGGER_POSITIONS = [
  { x: 4000, caveId: 'cave-1' },
  { x: 8500, caveId: 'cave-2' },
  { x: 13000, caveId: 'cave-3' },
  { x: 17500, caveId: 'cave-4' },
  { x: 22500, caveId: 'cave-5' },
] as const;

const TRIGGER_Y = GROUND_Y - 50;
const TRIGGER_Y_FLORESTA = GROUND_Y - 86;
const TRIGGER_Y_ESPACO = 555;


// ─── GameScene Class ─────────────────────────────────────────────────────────

export class GameScene extends Phaser.Scene {
  private mapId: string = '';
  private parallaxLayers: ParallaxLayerInfo[] = [];
  private tilemapLayer: Phaser.GameObjects.Group | null = null;
  private levelResult: ProceduralLevelResult | null = null;
  private player: Phaser.Physics.Arcade.Sprite | null = null;
  private orientationTimer: Phaser.Time.TimerEvent | null = null;
  private currentOrientation: 'landscape' | 'portrait' = 'landscape';
  private lastSafeX: number = 200;
  private lastSafeY: number = 400;
  private playerFacingRight: boolean = true;
  private resizeHandler: (() => void) | null = null;
  private triggers: TriggerInfo[] = [];
  private keyE: Phaser.Input.Keyboard.Key | null = null;
  private overlayActive: boolean = false;
  private currentOverlapTriggerIndex: number = -1;
  private overlayBridge: ChallengeOverlayBridge | null = null;
  private gameStateCoordinator: GameStateCoordinator | null = null;

  // ─── Enemies ─────────────────────────────────────────────────────────────
  private enemies: EnemyInfo[] = [];

  // ─── Power-Ups ───────────────────────────────────────────────────────────
  private powerUps: PowerUpInfo[] = [];
  private isInvincible: boolean = false;
  private invincibilityTimer: Phaser.Time.TimerEvent | null = null;
  private nextJumpBoosted: boolean = false;

  // ─── Fireball Attack ─────────────────────────────────────────────────────
  private hasFireball: boolean = false;
  private fireballTimer: Phaser.Time.TimerEvent | null = null;
  private fireballIndicator: Phaser.GameObjects.Text | null = null;
  private fireballCooldown: number = 0;
  private fireballProjectiles: Phaser.GameObjects.Arc[] = [];
  private keyF: Phaser.Input.Keyboard.Key | null = null;

  // ─── Shield Effect ───────────────────────────────────────────────────────
  private hasShield: boolean = false;
  private shieldTimer: Phaser.Time.TimerEvent | null = null;

  // ─── Touch Controls ──────────────────────────────────────────────────────
  private touchLeftBtn: Phaser.GameObjects.Rectangle | null = null;
  private touchRightBtn: Phaser.GameObjects.Rectangle | null = null;
  private touchJumpBtn: Phaser.GameObjects.Rectangle | null = null;
  private touchEBtn: Phaser.GameObjects.Rectangle | null = null;
  private touchLeftPressed: boolean = false;
  private touchRightPressed: boolean = false;
  private touchJumpPressed: boolean = false;
  private touchEPressed: boolean = false;

  // ─── Sound played flags ──────────────────────────────────────────────────
  private sparklePlayedForTrigger: number = -1;

  // ─── Collectible System ──────────────────────────────────────────────────
  private collectibleSystem: CollectibleSystem | null = null;
  private collectiblesHUD: CollectiblesHUD | null = null;
  private finishTriggerSystem: FinishTriggerSystem | null = null;
  private collectibleEntities: Collectible[] = [];
  private portalSprite: Phaser.GameObjects.Image | null = null;
  private finishTriggerReached: boolean = false;
  private fireballCountdownText: Phaser.GameObjects.Text | null = null;
  private fireballCountdownEndTime: number = 0;

  constructor() {
    super({ key: 'GameScene' });
  }

  // ─── Phaser Lifecycle ────────────────────────────────────────────────────

  init(data: GameSceneData): void {
    this.mapId = data.mapId ?? '';
  }

  create(): void {
    this.setupPhysics();
    this.setupParallaxLayers();
    this.setupTilemapPlaceholder();
    this.setupPlayerPlaceholder();
    this.setupCamera();
    this.setupKeyboardControls();
    this.setupChallengeTriggers();
    this.setupChallengeSystem();
    this.setupOrientationListener();
    this.applyViewportSafeArea();
    this.setupMusic();
    this.setupAudioUnlock();
    this.setupTouchControls();
    this.setupEnemies();
    this.setupPowerUps();
    this.restoreProgress();
    this.setupCollectibles();
    this.setupCollectiblesHUD();
    this.setupFinishTrigger();
    this.setupHUD();
    this.playMapEntryAnimation();
    this.showTutorialIfFirstTime();
    this.setupPortraitWarning();
    this.setupFullscreen();
  }

  update(time: number, delta: number): void {
    this.updateParallax();
    this.updateKeyboardInput(time);
    this.updateTriggerOverlaps();
    this.updateEnemies(delta);
    this.updatePowerUps(time);
    this.updateFireballs();
    this.updateFireballIndicator();
    this.updateCollectibles(delta);
    this.updateCollectiblesHUD();
    this.updateFinishTrigger();
  }

  // ─── Keyboard Controls (Desktop) ──────────────────────────────────────────

  private cursors: Phaser.Types.Input.Keyboard.CursorKeys | null = null;

  private setupKeyboardControls(): void {
    if (this.input.keyboard) {
      this.cursors = this.input.keyboard.createCursorKeys();
      this.keyE = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E);
      this.keyF = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F);
    }
  }

  private updateKeyboardInput(_time: number): void {
    if (!this.player || !this.cursors || this.overlayActive) return;

    const body = this.player.body as Phaser.Physics.Arcade.Body;
    if (!body) return;

    const speed = 250;

    // Safety: reset touch states if no pointers are actively down (prevents stuck buttons)
    if (this.sys.game.device.input.touch) {
      let anyPointerDown = false;
      for (const pointer of this.input.manager.pointers) {
        if (pointer.isDown) { anyPointerDown = true; break; }
      }
      if (!anyPointerDown) {
        this.touchLeftPressed = false;
        this.touchRightPressed = false;
        this.touchJumpPressed = false;
      }
    }

    const leftDown = this.cursors.left.isDown || this.touchLeftPressed;
    const rightDown = this.cursors.right.isDown || this.touchRightPressed;
    const jumpDown = this.cursors.space.isDown || this.cursors.up.isDown || this.touchJumpPressed;

    // Horizontal movement with walk animation
    if (leftDown) {
      body.setVelocityX(-speed);
      this.playerFacingRight = false;
      if (body.blocked.down) this.animateWalk(_time);
    } else if (rightDown) {
      body.setVelocityX(speed);
      this.playerFacingRight = true;
      if (body.blocked.down) this.animateWalk(_time);
    } else {
      body.setVelocityX(0);
      if (body.blocked.down) {
        (this.player as Phaser.GameObjects.Sprite).setTexture('char-idle');
        (this.player as Phaser.GameObjects.Sprite).setFlipX(this.playerFacingRight);
      }
    }

    // Jump
    if (jumpDown && body.blocked.down) {
      let jumpVelocity = -650;
      if (this.nextJumpBoosted) {
        jumpVelocity = -975; // 50% higher
        this.nextJumpBoosted = false;
      }
      body.setVelocityY(jumpVelocity);
    }

    // Airborne textures
    if (!body.blocked.down && body.velocity.y < -50) {
      (this.player as Phaser.GameObjects.Sprite).setTexture('char-jump');
      (this.player as Phaser.GameObjects.Sprite).setFlipX(!this.playerFacingRight);
    } else if (!body.blocked.down && body.velocity.y > 50) {
      (this.player as Phaser.GameObjects.Sprite).setTexture('char-fall');
      (this.player as Phaser.GameObjects.Sprite).setFlipX(this.playerFacingRight);
    }

    // Track last safe position (when on ground)
    if (body.blocked.down) {
      this.lastSafeX = this.player.x;
      this.lastSafeY = this.player.y;
    }

    // Fall detection
    if (this.player.y > BASE_HEIGHT - 20) {
      this.player.setPosition(this.lastSafeX, this.lastSafeY - 50);
      body.setVelocity(0, 0);
    }

    // Clamp player within world bounds
    if (this.player.x < 24) {
      this.player.x = 24;
      body.setVelocityX(0);
    }
    const maxX = (this.levelResult?.worldWidth ?? 25000) - 24;
    if (this.player.x > maxX) {
      this.player.x = maxX;
      body.setVelocityX(0);
    }

    // End of level detection
    if (this.player.x > (this.levelResult?.worldWidth ?? 25000) - 200 && !(this as any)._levelEndShown) {
      (this as any)._levelEndShown = true;
      this.showLevelComplete();
    }

    // E key — interact with trigger
    if (this.keyE && Phaser.Input.Keyboard.JustDown(this.keyE)) {
      this.handleTriggerInteraction();
    }
    // Touch E button
    if (this.touchEPressed) {
      this.touchEPressed = false;
      this.handleTriggerInteraction();
    }

    // F key — shoot fireball
    if (this.keyF && Phaser.Input.Keyboard.JustDown(this.keyF) && this.hasFireball) {
      this.shootFireball(_time);
    }
  }

  private walkFrames = ['char-walk1', 'char-walk3'];
  private walkFrameIndex = 0;
  private walkTimer = 0;
  private readonly WALK_FRAME_DURATION = 200;

  private animateWalk(time: number): void {
    if (time - this.walkTimer > this.WALK_FRAME_DURATION) {
      this.walkTimer = time;
      this.walkFrameIndex = (this.walkFrameIndex + 1) % this.walkFrames.length;
    }
    (this.player as Phaser.GameObjects.Sprite).setTexture(this.walkFrames[this.walkFrameIndex]);
    (this.player as Phaser.GameObjects.Sprite).setFlipX(!this.playerFacingRight);
  }

  // ─── Physics Setup ───────────────────────────────────────────────────────

  private setupPhysics(): void {
    this.physics.world.gravity.y = GRAVITY_Y;
  }

  // ─── Parallax Background ────────────────────────────────────────────────

  private setupParallaxLayers(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor('#87CEEB');
    this.parallaxLayers = [];

    // Choose background based on mapId
    let bgKey = 'forest-bg-full';
    if (this.mapId === 'montanha-gelada') {
      bgKey = this.textures.exists('montanha-bg') ? 'montanha-bg' : 'ice-aurora-sky';
    } else if (this.mapId === 'reino-encantado') {
      bgKey = 'reino-bg';
    } else if (this.mapId === 'espaco-sideral') {
      bgKey = 'espaco-bg';
      this.cameras.main.setBackgroundColor('#0a0a2e');
    } else {
      bgKey = this.textures.exists('floresta-bg') ? 'floresta-bg' : 'forest-bg-full';
    }

    if (this.textures.exists(bgKey)) {
      // With EXPAND mode, use camera viewport dimensions to cover full visible area
      const camW = this.cameras.main.width;
      const camH = this.cameras.main.height;
      const bg = this.add.image(camW / 2, camH / 2, bgKey);
      bg.setDisplaySize(camW, camH);
      bg.setDepth(LAYER_DEPTHS.BACKGROUND_SKY);
      bg.setScrollFactor(0, 0);
      this.parallaxLayers.push({ image: bg, scrollFactor: 0, depth: LAYER_DEPTHS.BACKGROUND_SKY });
    }
  }

  private updateParallax(): void {
    // Background is fixed (scrollFactor 0) — no update needed
  }

  // ─── Tilemap Placeholder ─────────────────────────────────────────────────

  private setupTilemapPlaceholder(): void {
    this.levelResult = generateLevel(this, this.mapId);
    this.tilemapLayer = this.add.group();

    const children = this.levelResult.platforms.getChildren();
    for (const child of children) {
      this.tilemapLayer.add(child);
      (child as Phaser.GameObjects.Rectangle).setDepth(LAYER_DEPTHS.TILEMAP);
    }
  }

  // ─── Player Setup ────────────────────────────────────────────────────────

  private setupPlayerPlaceholder(): void {
    const startX = 200;
    const startY = 300;

    const playerSprite = this.add.sprite(startX, startY, 'char-idle');
    playerSprite.setDisplaySize(100, 140);
    playerSprite.setDepth(LAYER_DEPTHS.PLAYER);

    this.physics.add.existing(playerSprite);
    const body = playerSprite.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(false);
    body.setSize(playerSprite.width * 0.6, playerSprite.height * 0.9);
    body.setOffset(playerSprite.width * 0.2, playerSprite.height * 0.1);

    this.player = playerSprite as Phaser.Physics.Arcade.Sprite;

    if (this.levelResult) {
      this.physics.add.collider(playerSprite, this.levelResult.platforms);
    }
  }

  // ─── Camera Setup ────────────────────────────────────────────────────────

  private setupCamera(): void {
    const worldWidth = this.levelResult?.worldWidth ?? 10240;
    const camera = this.cameras.main;
    camera.setBounds(0, 0, worldWidth, BASE_HEIGHT);

    if (this.player) {
      camera.startFollow(this.player, true, CAMERA_LERP, CAMERA_LERP);
    }
    camera.setDeadzone(100, 50);
  }


  // ─── Challenge Triggers ──────────────────────────────────────────────────

  private setupChallengeTriggers(): void {
    this.triggers = [];

    // Use world-specific trigger positions (placed on platforms)
    const positions = TRIGGER_POSITIONS_MAP[this.mapId] ?? TRIGGER_POSITIONS_MAP['floresta-encantada'] ?? TRIGGER_POSITIONS;

    for (let i = 0; i < positions.length; i++) {
      const def = positions[i];
      const key = getTriggerKey(this.mapId, i);

      if (!this.textures.exists(key)) {
        continue;
      }

      const triggerX = def.x;
      const triggerYPos = this.mapId === 'espaco-sideral' ? TRIGGER_Y_ESPACO : (this.mapId === 'floresta-encantada' || this.mapId === 'reino-encantado') ? TRIGGER_Y_FLORESTA : TRIGGER_Y;

      const sprite = this.add.image(triggerX, triggerYPos, key);
      sprite.setDisplaySize(280, 280);
      sprite.setDepth(LAYER_DEPTHS.TRIGGERS);

      const promptText = this.add.text(triggerX, triggerYPos - 50, 'Entrar? [E]', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '18px',
        color: '#ffffff',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        padding: { x: 8, y: 4 },
        align: 'center',
      });
      promptText.setOrigin(0.5);
      promptText.setDepth(LAYER_DEPTHS.HUD);
      promptText.setVisible(false);

      this.triggers.push({
        sprite,
        caveId: def.caveId,
        x: triggerX,
        completed: false,
        promptText,
        challengeData: {},
      });
    }
  }

  private updateTriggerOverlaps(): void {
    if (!this.player || this.overlayActive) return;

    const playerBounds = this.player.getBounds();
    let foundOverlap = false;

    for (let i = 0; i < this.triggers.length; i++) {
      const trigger = this.triggers[i];
      if (trigger.completed) {
        trigger.promptText?.setVisible(false);
        continue;
      }

      const triggerBounds = trigger.sprite.getBounds();

      if (Phaser.Geom.Intersects.RectangleToRectangle(playerBounds, triggerBounds)) {
        trigger.promptText?.setVisible(true);
        this.currentOverlapTriggerIndex = i;
        foundOverlap = true;

        // Play sparkle sound once per trigger overlap
        if (this.sparklePlayedForTrigger !== i) {
          this.sparklePlayedForTrigger = i;
          this.playSound('sparkle');
        }

        // Show touch E button if on mobile
        if (this.touchEBtn) {
          this.touchEBtn.setVisible(true);
        }
      } else {
        trigger.promptText?.setVisible(false);
      }
    }

    if (!foundOverlap) {
      this.currentOverlapTriggerIndex = -1;
      this.sparklePlayedForTrigger = -1;
      if (this.touchEBtn) {
        this.touchEBtn.setVisible(false);
      }
    }
  }

  private handleTriggerInteraction(): void {
    if (this.currentOverlapTriggerIndex < 0 || this.overlayActive) return;

    const trigger = this.triggers[this.currentOverlapTriggerIndex];
    if (!trigger || trigger.completed) return;

    // Play click sound
    this.playSound('click');

    // Determine standalone path based on mapId and cave number
    const mapPrefix: Record<string, string> = {
      'floresta-encantada': 'floresta',
      'montanha-gelada': 'montanha',
      'reino-encantado': 'reino',
      'espaco-sideral': 'espaco',
    };
    const prefix = mapPrefix[this.mapId] ?? 'floresta';
    const caveNum = trigger.caveId.replace('cave-', '');
    const standaloneUrl = `/standalone/${prefix}-cave-${caveNum}.html`;

    this.overlayActive = true;

    // Launch challenge directly (no transition animation)
    this.launchStandaloneChallenge(standaloneUrl, trigger.caveId);
  }

  /**
   * Plays the cave entry transition animation:
   * Fades out the game, displays the front-facing character centered on screen,
   * then fades it out and calls the callback to launch the challenge.
   */
  private playCaveEntryTransition(onComplete: () => void): void {
    const { width, height } = this.scale;

    // Create a black overlay background fixed to camera
    const transitionBg = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0);
    transitionBg.setScrollFactor(0);
    transitionBg.setDepth(LAYER_DEPTHS.HUD + 10);

    // Create the front-facing character image (if texture available)
    let characterImage: Phaser.GameObjects.Image | null = null;
    if (this.textures.exists('personagem-frente')) {
      characterImage = this.add.image(width / 2, height / 2, 'personagem-frente');
      // Scale down from 512×512 to fit nicely on screen (approx 256×256)
      const targetSize = Math.min(width, height) * 0.4;
      characterImage.setDisplaySize(targetSize, targetSize);
      characterImage.setScrollFactor(0);
      characterImage.setDepth(LAYER_DEPTHS.HUD + 11);
      characterImage.setAlpha(0);
    }

    // Step 1: Fade in the black background
    this.tweens.add({
      targets: transitionBg,
      alpha: 0.9,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        // Step 2: Fade in the character image
        if (characterImage) {
          this.tweens.add({
            targets: characterImage,
            alpha: 1,
            scale: characterImage.scale * 1.05,
            duration: 300,
            ease: 'Back.easeOut',
            onComplete: () => {
              // Step 3: Hold for a moment, then fade out and launch challenge
              this.time.delayedCall(600, () => {
                this.tweens.add({
                  targets: [characterImage, transitionBg],
                  alpha: 0,
                  duration: 250,
                  ease: 'Power2',
                  onComplete: () => {
                    // Clean up transition elements
                    characterImage?.destroy();
                    transitionBg.destroy();
                    onComplete();
                  },
                });
              });
            },
          });
        } else {
          // No character image available — just proceed after a brief pause
          this.time.delayedCall(300, () => {
            transitionBg.destroy();
            onComplete();
          });
        }
      },
    });
  }

  /**
   * Launches a standalone challenge HTML in a fullscreen iframe overlay.
   * Listens for completion/exit via postMessage.
   */
  private launchStandaloneChallenge(url: string, caveId: string): void {
    if (typeof document === 'undefined') return;

    // Try to lock orientation to portrait via Fullscreen + Screen Orientation API
    const lockPortrait = async () => {
      try {
        await document.documentElement.requestFullscreen();
        await (screen.orientation as any).lock('portrait');
      } catch (_e) {
        // Silently fail — not all browsers support this
      }
    };
    if (this.sys.game.device.input.touch) {
      lockPortrait();
    }

    // Create overlay container
    const overlay = document.createElement('div');
    overlay.id = 'standalone-challenge-overlay';
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      z-index: 1000; background: #000;
    `;

    // Create iframe — caves work best in portrait, user rotates phone naturally
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.style.cssText = `
      width: 100%; height: 100%; border: none;
    `;
    overlay.appendChild(iframe);

    // Exit button (top-right)
    const exitBtn = document.createElement('button');
    exitBtn.textContent = '✕';
    exitBtn.style.cssText = `
      position: fixed; top: 12px; right: 12px; z-index: 1001;
      width: 44px; height: 44px; border-radius: 50%;
      border: 2px solid rgba(255,255,255,0.4); background: rgba(0,0,0,0.5);
      color: #fff; font-size: 20px; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
    `;
    overlay.appendChild(exitBtn);

    // "Voltar ao Mundo" button (appears after 20s)
    const returnBtn = document.createElement('button');
    returnBtn.id = 'awe-return-btn';
    returnBtn.textContent = '✓ Voltar ao Mundo';
    returnBtn.style.cssText = `
      position: fixed; bottom: 20px; right: 20px; z-index: 1001;
      padding: 16px 32px; font-size: 20px; font-weight: bold;
      background: #38B000; color: white; border: none; border-radius: 12px;
      cursor: pointer; display: none;
    `;
    overlay.appendChild(returnBtn);

    document.body.appendChild(overlay);

    // Show return button after 20s
    const returnTimer = setTimeout(() => {
      returnBtn.style.display = 'block';
    }, 20000);

    // Cleanup function
    const cleanup = (markComplete: boolean) => {
      clearTimeout(returnTimer);
      window.removeEventListener('message', messageHandler);
      overlay.remove();
      this.overlayActive = false;

      // Unlock orientation and exit fullscreen, then re-lock to landscape
      try {
        (screen.orientation as any).unlock();
      } catch (_e) { /* ignore */ }
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      // Re-enter fullscreen + landscape for the overworld
      setTimeout(async () => {
        try {
          await document.documentElement.requestFullscreen();
          await (screen.orientation as any).lock('landscape');
        } catch (_e) { /* ignore */ }
      }, 300);

      if (markComplete) {
        const trigger = this.triggers.find((t) => t.caveId === caveId);
        if (trigger) {
          trigger.completed = true;
          trigger.sprite.setAlpha(0.4);
          trigger.promptText?.setVisible(false);
          this.tweens.killTweensOf(trigger.sprite);
        }
        this.saveProgress();
        this.updateHUD();
        this.checkAllCavesComplete();
      }
    };

    // Listen for completion message from standalone
    const messageHandler = (event: MessageEvent) => {
      const { type } = event.data || {};
      if (type === 'eduadventure-complete') {
        cleanup(true);
      } else if (type === 'eduadventure-exit') {
        cleanup(false);
      }
    };
    window.addEventListener('message', messageHandler);

    // Exit button click
    exitBtn.addEventListener('click', () => cleanup(false));

    // Return button click (mark as complete)
    returnBtn.addEventListener('click', () => cleanup(true));
  }

  // ─── Challenge System ────────────────────────────────────────────────────

  private setupChallengeSystem(): void {
    this.gameStateCoordinator = new GameStateCoordinator();
    this.gameStateCoordinator.transitionTo('MAP_SELECT');
    this.gameStateCoordinator.transitionTo('OVERWORLD');

    this.gameStateCoordinator.setPhaserCallbacks({
      pauseScene: () => {
        this.scene.pause();
      },
      resumeScene: () => {
        this.scene.resume();
        this.overlayActive = false;
      },
      blockInput: () => {
        this.overlayActive = true;
      },
      unblockInput: () => {
        this.overlayActive = false;
      },
      playEntryAnimation: async (_caveId: string) => {
        await new Promise<void>((resolve) => {
          this.cameras.main.fadeOut(200, 0, 0, 0);
          this.time.delayedCall(200, () => resolve());
        });
      },
      playExitAnimation: async (_caveId: string) => {
        await new Promise<void>((resolve) => {
          this.cameras.main.fadeIn(200, 0, 0, 0);
          this.time.delayedCall(200, () => resolve());
        });
      },
    });

    // Create overlay bridge with IframeMotorFactory
    if (typeof document !== 'undefined') {
      const container = document.getElementById('phaser-container') ?? document.body;
      this.overlayBridge = new ChallengeOverlayBridge({
        container,
        motorFactory: new IframeMotorFactory(),
        onChallengeComplete: (caveId: string) => {
          // Stop motor and hide overlay FIRST
          this.overlayBridge?.stopMotor();
          this.overlayBridge?.hide();

          // Mark trigger as completed
          const trigger = this.triggers.find((t) => t.caveId === caveId);
          if (trigger) {
            trigger.completed = true;
            trigger.sprite.setAlpha(0.4);
            trigger.promptText?.setVisible(false);
            this.tweens.killTweensOf(trigger.sprite);
          }
          this.overlayActive = false;
          this.saveProgress();
          this.updateHUD();
          this.checkAllCavesComplete();

          // Remove any lingering "Voltar ao Mundo" button
          const returnBtn = document.querySelector('#awe-return-btn');
          if (returnBtn) returnBtn.remove();
        },
        onChallengeExit: () => {
          // Stop motor and hide overlay
          this.overlayBridge?.stopMotor();
          this.overlayBridge?.hide();
          this.overlayActive = false;

          // Remove any lingering "Voltar ao Mundo" button
          const returnBtn = document.querySelector('#awe-return-btn');
          if (returnBtn) returnBtn.remove();
        },
      });

      this.gameStateCoordinator.setOverlayCallbacks(this.overlayBridge);
    }
  }


  // ─── Feature 2: Music and Sounds ─────────────────────────────────────────

  private setupMusic(): void {
    // Choose music based on map
    let musicKey: string;
    switch (this.mapId) {
      case 'montanha-gelada':
        musicKey = 'ice-mountain-ambient';
        break;
      case 'reino-encantado':
        musicKey = 'menu-music'; // placeholder until reino ambient is available
        break;
      case 'espaco-sideral':
        musicKey = 'menu-music'; // placeholder until espaco ambient is available
        break;
      default:
        musicKey = 'forest-ambient';
        break;
    }
    if (this.sound.get(musicKey) === null && this.cache.audio.exists(musicKey)) {
      this.sound.add(musicKey, { loop: true, volume: 0.3 }).play();
    }
  }

  /**
   * Helper to play a sound safely (checks if key exists).
   */
  private playSound(key: string, volume: number = 0.5): void {
    if (this.cache.audio.exists(key)) {
      this.sound.play(key, { volume });
    }
  }

  // ─── HUD — Cave progress indicator ──────────────────────────────────────

  private hudText: Phaser.GameObjects.Text | null = null;

  private setupHUD(): void {
    this.hudText = this.add.text(20, 20, this.getHUDString(), {
      fontFamily: 'Arial, sans-serif',
      fontSize: '20px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3,
    });
    this.hudText.setScrollFactor(0);
    this.hudText.setDepth(LAYER_DEPTHS.HUD);
  }

  private updateHUD(): void {
    if (this.hudText) {
      this.hudText.setText(this.getHUDString());
    }
  }

  private getHUDString(): string {
    const total = this.triggers.length;
    const completed = this.triggers.filter((t) => t.completed).length;
    const icons = this.triggers.map((t) => t.completed ? '✅' : '⬜').join('');
    return `${icons}  ${completed}/${total}`;
  }

  // ─── Feature 3: Touch Controls for Mobile ────────────────────────────────

  private setupTouchControls(): void {
    // Only show on touch devices
    if (!this.sys.game.device.input.touch) return;

    // Fix 5: Enable multi-touch (3 pointers for left+jump+E simultaneously)
    this.input.addPointer(2);

    // Use scale.width which reflects expanded dimensions
    const actualW = this.scale.width;
    const btnRadius = 60;
    const margin = 20;
    const bottomY = BASE_HEIGHT - margin - btnRadius;
    const alpha = 0.45;

    // Helper: create a circular button
    const makeCircleBtn = (x: number, y: number, color: number, label: string, fontSize = '40px') => {
      const circle = this.add.circle(x, y, btnRadius, color, alpha);
      circle.setScrollFactor(0);
      circle.setDepth(LAYER_DEPTHS.HUD);
      circle.setInteractive(new Phaser.Geom.Circle(btnRadius, btnRadius, btnRadius), Phaser.Geom.Circle.Contains);
      const txt = this.add.text(x, y, label, { fontSize, color: '#ffffff', align: 'center', fontStyle: 'bold' })
        .setOrigin(0.5).setScrollFactor(0).setDepth(LAYER_DEPTHS.HUD + 1);
      return { circle, txt };
    };

    // Left arrow button (bottom-left)
    const leftX = margin + btnRadius;
    const { circle: leftBtn } = makeCircleBtn(leftX, bottomY, 0x333333, '←');
    this.touchLeftBtn = leftBtn as any;
    leftBtn.on('pointerdown', () => { this.touchLeftPressed = true; });
    leftBtn.on('pointerup', () => { this.touchLeftPressed = false; });

    // Right arrow button (next to left)
    const rightX = leftX + btnRadius * 2 + margin;
    const { circle: rightBtn } = makeCircleBtn(rightX, bottomY, 0x333333, '→');
    this.touchRightBtn = rightBtn as any;
    rightBtn.on('pointerdown', () => { this.touchRightPressed = true; });
    rightBtn.on('pointerup', () => { this.touchRightPressed = false; });

    // Jump button (far bottom-right)
    const jumpX = actualW - margin - btnRadius;
    const { circle: jumpBtn } = makeCircleBtn(jumpX, bottomY, 0x4444aa, '↑');
    this.touchJumpBtn = jumpBtn as any;
    jumpBtn.on('pointerdown', () => { this.touchJumpPressed = true; });
    jumpBtn.on('pointerup', () => { this.touchJumpPressed = false; });

    // E button (left of jump, appears only near triggers)
    const eBtnX = jumpX - btnRadius * 2 - margin;
    const { circle: eBtn, txt: eLabel } = makeCircleBtn(eBtnX, bottomY, 0x44aa44, 'E');
    this.touchEBtn = eBtn as any;
    eBtn.setVisible(false);
    eLabel.setVisible(false);
    (this.touchEBtn as any)._label = eLabel;

    eBtn.on('pointerdown', () => { this.touchEPressed = true; });
    eBtn.on('pointerup', () => { });

    const origSetVisible = this.touchEBtn.setVisible.bind(this.touchEBtn);
    this.touchEBtn.setVisible = (value: boolean) => {
      origSetVisible(value);
      eLabel.setVisible(value);
      return this.touchEBtn!;
    };
    this.touchEBtn.setVisible(false);

    // Fireball button (above jump) — visible when player has fireball
    const fireY = bottomY - btnRadius * 2 - margin;
    const { circle: fireBtn, txt: fireLabel } = makeCircleBtn(jumpX, fireY, 0xcc4400, '🔥');
    fireBtn.setVisible(false);
    fireLabel.setVisible(false);

    fireBtn.on('pointerdown', () => {
      if (this.hasFireball) this.shootFireball(this.time.now);
    });
    fireBtn.on('pointerup', () => { });

    // Store reference for visibility toggling in update
    (this as any)._touchFireBtn = fireBtn;
    (this as any)._touchFireLabel = fireLabel;

    // Safety: reset all pressed states when ALL pointers are up (prevents stuck buttons)
    this.input.on('pointerup', () => {
      // Check if any pointer is still down on any button
      const pointers = this.input.manager.pointers;
      let anyDown = false;
      for (const p of pointers) {
        if (p.isDown) { anyDown = true; break; }
      }
      if (!anyDown) {
        this.touchLeftPressed = false;
        this.touchRightPressed = false;
        this.touchJumpPressed = false;
      }
    });
  }

  // ─── Fix 3: Portrait Mode Warning ───────────────────────────────────────

  private setupPortraitWarning(): void {
    // Removed — orientation is the user's choice
  }

  private rotatePrompt: Phaser.GameObjects.Container | null = null;

  private showRotatePrompt(): void {
    // Removed
  }

  private hideRotatePrompt(): void {
    // Removed
  }

  // ─── Fix 4: Fullscreen on First Tap ─────────────────────────────────────

  private setupFullscreen(): void {
    // On touch devices: first tap triggers fullscreen + landscape lock
    // Re-arms if fullscreen is exited (e.g. by rotating to portrait)
    if (!this.sys.game.device.input.touch) return;

    const enterFullscreenLandscape = async () => {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        }
        await (screen.orientation as any).lock('landscape');
      } catch (_e) { /* not all browsers support this */ }
    };

    // First tap activates fullscreen
    const firstTapHandler = () => {
      enterFullscreenLandscape();
      this.input.off('pointerdown', firstTapHandler);
    };
    this.input.on('pointerdown', firstTapHandler);

    // Re-arm: when fullscreen is lost, next tap re-enters fullscreen
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement) {
        // Fullscreen was exited — re-arm on next tap
        const rearmHandler = () => {
          enterFullscreenLandscape();
          this.input.off('pointerdown', rearmHandler);
        };
        this.input.on('pointerdown', rearmHandler);
      }
    });
  }

  // ─── Fix 7: Audio Unlock Gesture ────────────────────────────────────────

  private setupAudioUnlock(): void {
    if (this.sound.locked) {
      const unlockHandler = () => {
        this.sound.unlock();
        this.input.off('pointerdown', unlockHandler);
      };
      this.input.on('pointerdown', unlockHandler);
    }
  }

  // ─── Feature 4: Enemies ──────────────────────────────────────────────────

  private setupEnemies(): void {
    this.enemies = [];

    // Choose enemy types and difficulty based on map
    let enemyTypeA: string;
    let enemyTypeB: string;
    let baseSpeed: number;
    let baseRange: number;

    switch (this.mapId) {
      case 'montanha-gelada':
        enemyTypeA = 'ice-enemy-penguin';
        enemyTypeB = 'ice-enemy-yeti';
        baseSpeed = 70;
        baseRange = 250;
        break;
      case 'reino-encantado':
        enemyTypeA = 'reino-rato';
        enemyTypeB = 'reino-morcego';
        baseSpeed = 75;
        baseRange = 260;
        break;
      case 'espaco-sideral':
        enemyTypeA = 'espaco-asteroide';
        enemyTypeB = 'espaco-alien';
        baseSpeed = 85;
        baseRange = 280;
        break;
      default:
        enemyTypeA = 'forest-enemy-snail';
        enemyTypeB = 'forest-enemy-beetle';
        baseSpeed = 50;
        baseRange = 200;
        break;
    }

    // Enemy size — large enough to be hit by fireballs
    const ENEMY_SIZE = 120;

    // Enemy placement — each enemy is placed on a specific platform
    // Format: { platX, platWidth, type, speedMult }
    // platX = center X of ground platform, platWidth = width in px
    // Enemy patrols within platX ± (platWidth/2 - 50)
    const T = 64;

    // Platform-linked enemy definitions per world
    const platformEnemyDefs: Record<string, Array<{ platX: number; platW: number; type: string; speedMult: number }>> = {
      'floresta-encantada': [
        // Zone 2 (s=5000): platforms at s+1600(10T), s+2300(12T), s+3100(10T)
        { platX: 6600, platW: T*10, type: enemyTypeA, speedMult: 1.0 },
        { platX: 7300, platW: T*12, type: enemyTypeA, speedMult: 1.0 },
        // Zone 3 (s=10000): platforms at s+1200(10T), s+2800(12T)
        { platX: 11200, platW: T*10, type: enemyTypeB, speedMult: 1.1 },
        { platX: 12800, platW: T*12, type: enemyTypeB, speedMult: 1.1 },
        // Zone 4 (s=15000): platforms at s+1200(8T), s+2700(12T), s+3600(10T)
        { platX: 16200, platW: T*8, type: enemyTypeA, speedMult: 1.2 },
        { platX: 17700, platW: T*12, type: enemyTypeA, speedMult: 1.2 },
        { platX: 18600, platW: T*10, type: enemyTypeB, speedMult: 1.3 },
        // Zone 5 (s=20000): platforms at s+1800(10T), s+2600(12T), s+3500(10T)
        { platX: 21800, platW: T*10, type: enemyTypeB, speedMult: 1.3 },
        { platX: 22600, platW: T*12, type: enemyTypeA, speedMult: 1.4 },
        { platX: 23500, platW: T*10, type: enemyTypeA, speedMult: 1.4 },
      ],
      'montanha-gelada': [
        // Zone 2: platforms at s+1050(7T), s+1700(8T), s+2400(7T), s+3750(10T)
        { platX: 6050, platW: T*7, type: enemyTypeA, speedMult: 1.0 },
        { platX: 6700, platW: T*8, type: enemyTypeA, speedMult: 1.1 },
        { platX: 8750, platW: T*10, type: enemyTypeB, speedMult: 1.0 },
        // Zone 3: s+1000(7T), s+2350(7T), s+2950(10T)
        { platX: 11000, platW: T*7, type: enemyTypeB, speedMult: 1.2 },
        { platX: 12350, platW: T*7, type: enemyTypeA, speedMult: 1.3 },
        { platX: 12950, platW: T*10, type: enemyTypeB, speedMult: 1.2 },
        // Zone 4: s+1000(6T), s+2200(8T), s+2850(10T), s+3600(7T)
        { platX: 16000, platW: T*6, type: enemyTypeA, speedMult: 1.4 },
        { platX: 17200, platW: T*8, type: enemyTypeB, speedMult: 1.3 },
        { platX: 17850, platW: T*10, type: enemyTypeA, speedMult: 1.5 },
        { platX: 18600, platW: T*7, type: enemyTypeB, speedMult: 1.4 },
        // Zone 5: s+950(6T), s+1500(7T), s+2700(10T), s+3450(7T)
        { platX: 20950, platW: T*6, type: enemyTypeB, speedMult: 1.5 },
        { platX: 21500, platW: T*7, type: enemyTypeA, speedMult: 1.6 },
        { platX: 22700, platW: T*10, type: enemyTypeB, speedMult: 1.5 },
        { platX: 23450, platW: T*7, type: enemyTypeA, speedMult: 1.7 },
        { platX: 24100, platW: T*6, type: enemyTypeB, speedMult: 1.6 },
      ],
      'reino-encantado': [
        // Zone 2: s+1000(7T), s+2250(8T), s+2900(9T), s+3600(8T)
        { platX: 6000, platW: T*7, type: enemyTypeA, speedMult: 1.0 },
        { platX: 7250, platW: T*8, type: enemyTypeA, speedMult: 1.1 },
        { platX: 7900, platW: T*9, type: enemyTypeB, speedMult: 1.0 },
        // Zone 3: s+1000(7T), s+2250(7T), s+2850(9T), s+3550(7T)
        { platX: 11000, platW: T*7, type: enemyTypeB, speedMult: 1.2 },
        { platX: 12250, platW: T*7, type: enemyTypeA, speedMult: 1.3 },
        { platX: 12850, platW: T*9, type: enemyTypeB, speedMult: 1.2 },
        // Zone 4: s+950(6T), s+2100(7T), s+2750(9T), s+3500(6T)
        { platX: 15950, platW: T*6, type: enemyTypeA, speedMult: 1.4 },
        { platX: 17100, platW: T*7, type: enemyTypeB, speedMult: 1.3 },
        { platX: 17750, platW: T*9, type: enemyTypeA, speedMult: 1.5 },
        { platX: 18500, platW: T*6, type: enemyTypeB, speedMult: 1.4 },
        // Zone 5: s+900(6T), s+2050(6T), s+2650(9T), s+3350(6T), s+3950(6T)
        { platX: 20900, platW: T*6, type: enemyTypeB, speedMult: 1.5 },
        { platX: 22050, platW: T*6, type: enemyTypeA, speedMult: 1.6 },
        { platX: 22650, platW: T*9, type: enemyTypeB, speedMult: 1.5 },
        { platX: 23350, platW: T*6, type: enemyTypeA, speedMult: 1.7 },
        { platX: 23950, platW: T*6, type: enemyTypeB, speedMult: 1.6 },
      ],
      'espaco-sideral': [
        // Zone 2: s+900(6T), s+1450(6T), s+2550(7T), s+3200(6T), s+3800(5T)
        { platX: 5900, platW: T*6, type: enemyTypeA, speedMult: 1.0 },
        { platX: 6450, platW: T*6, type: enemyTypeA, speedMult: 1.1 },
        { platX: 7550, platW: T*7, type: enemyTypeB, speedMult: 1.0 },
        // Zone 3: s+850(5T), s+1350(6T), s+2450(7T), s+3100(5T)
        { platX: 10850, platW: T*5, type: enemyTypeB, speedMult: 1.2 },
        { platX: 11350, platW: T*6, type: enemyTypeA, speedMult: 1.3 },
        { platX: 12450, platW: T*7, type: enemyTypeB, speedMult: 1.2 },
        // Zone 4: s+800(5T), s+1300(5T), s+2350(6T), s+2950(5T), s+3500(5T)
        { platX: 15800, platW: T*5, type: enemyTypeA, speedMult: 1.4 },
        { platX: 16300, platW: T*5, type: enemyTypeB, speedMult: 1.3 },
        { platX: 17350, platW: T*6, type: enemyTypeA, speedMult: 1.5 },
        { platX: 17950, platW: T*5, type: enemyTypeB, speedMult: 1.4 },
        // Zone 5: s+800(4T), s+1250(5T), s+2200(6T), s+2800(5T), s+3350(4T)
        { platX: 20800, platW: T*4, type: enemyTypeB, speedMult: 1.5 },
        { platX: 21250, platW: T*5, type: enemyTypeA, speedMult: 1.6 },
        { platX: 22200, platW: T*6, type: enemyTypeB, speedMult: 1.5 },
        { platX: 22800, platW: T*5, type: enemyTypeA, speedMult: 1.7 },
        { platX: 23350, platW: T*4, type: enemyTypeB, speedMult: 1.6 },
      ],
    };

    const enemyDefs = platformEnemyDefs[this.mapId] ?? platformEnemyDefs['floresta-encantada'];

    for (const def of enemyDefs) {
      if (!this.textures.exists(def.type)) continue;

      const isYeti = def.type === 'ice-enemy-yeti';
      const isSnail = def.type === 'forest-enemy-snail';
      const size = isYeti ? 200 : ENEMY_SIZE;
      // Y per enemy type: Snail=690, Yeti=685, others=670
      const enemyY = isSnail ? 690 : isYeti ? 685 : 670;
      const sprite = this.add.image(def.platX, enemyY, def.type);
      sprite.setDisplaySize(size, size);
      sprite.setOrigin(0.5, 1); // Anchor at bottom-center (feet)
      sprite.setDepth(LAYER_DEPTHS.ENEMIES);

      // Enable physics on the enemy
      this.physics.add.existing(sprite, false);
      const body = sprite.body as Phaser.Physics.Arcade.Body;
      body.setImmovable(true);
      body.setAllowGravity(false);
      // Hitbox: Yeti 100×140, others 60×84
      const hitboxW = isYeti ? 100 : 60;
      const hitboxH = isYeti ? 140 : 84;
      body.setSize(hitboxW, hitboxH);
      body.setOffset((size - hitboxW) / 2, size - hitboxH); // Align hitbox to bottom-center

      // Patrol range inherited from platform width
      const isFlying = def.type === 'reino-morcego';
      const effectiveRange = isFlying ? baseRange : Math.max(50, def.platW / 2 - 50);

      // Initial flip: PNGs that face left need flipX when starting direction is right (1)
      const nativeFacesLeft = def.type === 'reino-rato' || def.type === 'espaco-asteroide' || def.type === 'ice-enemy-penguin' || def.type === 'forest-enemy-beetle' || def.type === 'forest-enemy-snail' || def.type === 'ice-enemy-yeti';
      if (nativeFacesLeft) {
        sprite.setFlipX(true);
      }

      this.enemies.push({
        sprite,
        startX: def.platX,
        direction: 1,
        range: effectiveRange,
        speed: baseSpeed * def.speedMult,
        alive: true,
      });
    }

    // Set up overlap detection with player — done manually in updateEnemies via getBounds
    }

  private updateEnemies(_delta: number): void {
    if (!this.player) return;
    const playerBounds = this.player.getBounds();

    for (const enemy of this.enemies) {
      if (!enemy.alive) continue;

      // Patrol: move left/right within range
      enemy.sprite.x += enemy.direction * enemy.speed * (_delta / 1000);

      // Sync physics body with sprite position
      const body = enemy.sprite.body as Phaser.Physics.Arcade.Body;
      if (body) {
        body.updateFromGameObject();
      }

      if (enemy.sprite.x > enemy.startX + enemy.range) {
        enemy.direction = -1;
        // These PNGs face left natively; all others face right
        const facesLeft = enemy.sprite.texture.key === 'reino-rato' || enemy.sprite.texture.key === 'espaco-asteroide' || enemy.sprite.texture.key === 'ice-enemy-penguin' || enemy.sprite.texture.key === 'forest-enemy-beetle' || enemy.sprite.texture.key === 'forest-enemy-snail' || enemy.sprite.texture.key === 'ice-enemy-yeti';
        enemy.sprite.setFlipX(facesLeft ? false : true);
      } else if (enemy.sprite.x < enemy.startX - enemy.range) {
        enemy.direction = 1;
        const facesLeft = enemy.sprite.texture.key === 'reino-rato' || enemy.sprite.texture.key === 'espaco-asteroide' || enemy.sprite.texture.key === 'ice-enemy-penguin' || enemy.sprite.texture.key === 'forest-enemy-beetle' || enemy.sprite.texture.key === 'forest-enemy-snail' || enemy.sprite.texture.key === 'ice-enemy-yeti';
        enemy.sprite.setFlipX(facesLeft ? true : false);
      }

      // Manual collision detection using display bounds
      const enemyBounds = enemy.sprite.getBounds();
      if (Phaser.Geom.Intersects.RectangleToRectangle(playerBounds, enemyBounds)) {
        this.handleEnemyCollision(enemy);
      }
    }
  }

  private handleEnemyCollision(enemy: EnemyInfo): void {
    if (!enemy.alive || !this.player) return;
    if (this.isInvincible) return;

    const playerBody = this.player.body as Phaser.Physics.Arcade.Body;
    if (!playerBody) return;

    // Check if player is stomping (falling onto enemy from above)
    // Player bottom must be in the top 40% of the enemy sprite and player must be falling
    const enemyBounds = enemy.sprite.getBounds();
    const playerBottom = this.player.y + (this.player.displayHeight / 2);
    const enemyTopZone = enemyBounds.top + enemyBounds.height * 0.4;

    if (playerBody.velocity.y > 0 && playerBottom < enemyTopZone) {
      // Stomp! Destroy enemy
      enemy.alive = false;
      this.playSound('enemy-squish');

      // Squish effect
      this.tweens.add({
        targets: enemy.sprite,
        scaleY: 0,
        alpha: 0,
        duration: 200,
        onComplete: () => {
          enemy.sprite.destroy();
        },
      });

      // Bounce player up slightly
      playerBody.setVelocityY(-250);
    } else {
      // Side collision — check shield first
      if (this.hasShield) {
        // Shield absorbs the hit
        this.hasShield = false;
        if (this.shieldTimer) {
          this.shieldTimer.destroy();
          this.shieldTimer = null;
        }
        if (this.player) {
          this.player.clearTint();
        }
        this.playSound('powerup-expire');
        return;
      }

      // No shield — reposition player to last safe point
      this.player.setPosition(this.lastSafeX, this.lastSafeY - 50);
      playerBody.setVelocity(0, 0);

      // Brief flash to indicate damage — ensure alpha resets to 1 after
      this.tweens.add({
        targets: this.player,
        alpha: 0.3,
        duration: 100,
        yoyo: true,
        repeat: 3,
        onComplete: () => {
          if (this.player) this.player.setAlpha(1);
        },
      });
    }
  }


  // ─── Feature 5: Power-Ups ────────────────────────────────────────────────

  private setupPowerUps(): void {
    this.powerUps = [];

    // Power-up placement definitions
    const powerUpDefs: Array<{ x: number; y: number; type: 'star' | 'fireball' | 'shield' | 'spring' }> = [
      // Zone 1 (0-5000): 2 stars on floating platforms
      { x: 800, y: GROUND_Y - TILE_SIZE * 3, type: 'star' },
      { x: 2800, y: GROUND_Y - TILE_SIZE * 3.5, type: 'star' },
      // Zone 2 (5000-10000): 1 fireball, 1 shield
      { x: 6000, y: GROUND_Y - TILE_SIZE * 3.5, type: 'fireball' },
      { x: 7700, y: GROUND_Y - TILE_SIZE * 3, type: 'shield' },
      // Zone 3 (10000-15000): 1 spring, 1 star
      { x: 10800, y: GROUND_Y - TILE_SIZE * 3.5, type: 'spring' },
      { x: 13300, y: GROUND_Y - TILE_SIZE * 3.5, type: 'star' },
      // Zone 4 (15000-20000): 1 fireball, 1 shield
      { x: 16200, y: GROUND_Y - TILE_SIZE * 3, type: 'fireball' },
      { x: 18500, y: GROUND_Y - TILE_SIZE * 3, type: 'shield' },
      // Zone 5 (20000-25000): 1 star, 1 spring
      { x: 21200, y: GROUND_Y - TILE_SIZE * 3.5, type: 'star' },
      { x: 23500, y: GROUND_Y - TILE_SIZE * 3, type: 'spring' },
    ];

    const textureMap: Record<string, string> = {
      star: 'powerup-star',
      fireball: 'powerup-fireball',
      shield: 'powerup-shield',
      spring: 'powerup-spring',
    };

    for (const def of powerUpDefs) {
      const textureKey = textureMap[def.type];
      if (!this.textures.exists(textureKey)) continue;

      const sprite = this.add.image(def.x, def.y, textureKey);
      sprite.setDisplaySize(48, 48);
      sprite.setDepth(LAYER_DEPTHS.POWER_UPS);

      // Enable physics for overlap detection
      this.physics.add.existing(sprite, true); // static body

      this.powerUps.push({
        sprite,
        type: def.type,
        startY: def.y,
        collected: false,
        floatTime: Math.random() * Math.PI * 2, // Random phase offset
      });
    }

    // Set up overlap detection with player
    if (this.player) {
      for (const pu of this.powerUps) {
        this.physics.add.overlap(
          this.player,
          pu.sprite,
          () => this.collectPowerUp(pu),
          undefined,
          this
        );
      }
    }
  }

  private updatePowerUps(time: number): void {
    // Floating animation for uncollected power-ups
    for (const pu of this.powerUps) {
      if (pu.collected) continue;
      pu.floatTime += 0.03;
      pu.sprite.y = pu.startY + Math.sin(pu.floatTime) * 8;
    }
  }

  private collectPowerUp(pu: PowerUpInfo): void {
    if (pu.collected) return;
    pu.collected = true;

    // Play collect sound
    this.playSound('powerup-collect');

    // Emit particles at collection point
    const particleColors: Record<string, number> = {
      star: 0xffd700,
      fireball: 0xff6600,
      shield: 0x4488ff,
      spring: 0x44ff44,
    };
    this.emitCollectParticles(pu.sprite.x, pu.sprite.y, particleColors[pu.type] ?? 0xffffff);

    // Destroy sprite
    pu.sprite.destroy();

    // Apply power-up effect
    switch (pu.type) {
      case 'star':
        this.activateInvincibility();
        break;
      case 'spring':
        this.nextJumpBoosted = true;
        // Show brief indicator
        if (this.player) {
          const indicator = this.add.text(this.player.x, this.player.y - 80, '⬆ Super Salto!', {
            fontSize: '16px', color: '#00ff00', fontStyle: 'bold',
          }).setOrigin(0.5).setDepth(LAYER_DEPTHS.HUD);
          this.tweens.add({
            targets: indicator,
            y: indicator.y - 30,
            alpha: 0,
            duration: 1500,
            onComplete: () => indicator.destroy(),
          });
        }
        break;
      case 'fireball':
        this.activateFireball();
        break;
      case 'shield':
        this.activateShield();
        break;
    }
  }

  private activateInvincibility(): void {
    this.isInvincible = true;

    // Player flashes during invincibility
    if (this.player) {
      const flashTween = this.tweens.add({
        targets: this.player,
        alpha: { from: 1, to: 0.4 },
        duration: 200,
        yoyo: true,
        repeat: 12, // ~5 seconds of flashing
        onComplete: () => {
          if (this.player) this.player.setAlpha(1);
        },
      });

      // Show indicator
      const indicator = this.add.text(this.player.x, this.player.y - 80, '⭐ Invencível!', {
        fontSize: '16px', color: '#ffd700', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(LAYER_DEPTHS.HUD);
      this.tweens.add({
        targets: indicator,
        y: indicator.y - 30,
        alpha: 0,
        duration: 1500,
        onComplete: () => indicator.destroy(),
      });

      // End invincibility after 5 seconds
      if (this.invincibilityTimer) {
        this.invincibilityTimer.destroy();
      }
      this.invincibilityTimer = this.time.delayedCall(5000, () => {
        this.isInvincible = false;
        if (this.player) {
          this.player.setAlpha(1);
          this.tweens.killTweensOf(this.player);
          this.player.setAlpha(1);
        }
        this.playSound('powerup-expire');
      });
    }
  }

  // ─── Feature: Fireball Attack ──────────────────────────────────────────────

  private activateFireball(): void {
    this.hasFireball = true;
    this.fireballCooldown = 0;

    // Show indicator
    if (this.player) {
      const indicator = this.add.text(this.player.x, this.player.y - 80, '🔥 Bola de Fogo! [F]', {
        fontSize: '16px', color: '#ff6600', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(LAYER_DEPTHS.HUD);
      this.tweens.add({
        targets: indicator,
        y: indicator.y - 30,
        alpha: 0,
        duration: 1500,
        onComplete: () => indicator.destroy(),
      });

      // Persistent indicator near player
      if (this.fireballIndicator) {
        this.fireballIndicator.destroy();
      }
      this.fireballIndicator = this.add.text(0, 0, '🔥', {
        fontSize: '20px',
      }).setDepth(LAYER_DEPTHS.HUD);
    }

    // Cancel previous timer if any
    if (this.fireballTimer) {
      this.fireballTimer.destroy();
    }

    // 15 second duration
    this.fireballTimer = this.time.delayedCall(15000, () => {
      this.hasFireball = false;
      if (this.fireballIndicator) {
        this.fireballIndicator.destroy();
        this.fireballIndicator = null;
      }
      this.playSound('powerup-expire');
    });
  }

  private shootFireball(time: number): void {
    if (!this.player || !this.hasFireball) return;

    // Cooldown check (500ms)
    if (time - this.fireballCooldown < 500) return;
    this.fireballCooldown = time;

    // Create fireball projectile (orange circle, 16px)
    const directionX = this.playerFacingRight ? 1 : -1;
    const fireball = this.add.circle(
      this.player.x + directionX * 30,
      this.player.y + 20,
      8,
      0xff6600
    );
    fireball.setDepth(LAYER_DEPTHS.ENEMIES + 1);

    // Add physics
    this.physics.add.existing(fireball);
    const body = fireball.body as Phaser.Physics.Arcade.Body;
    body.setAllowGravity(false);
    body.setVelocityX(directionX * 400);

    // Store reference for cleanup
    this.fireballProjectiles.push(fireball);

    // Set up overlap with enemies
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue;
      this.physics.add.overlap(
        fireball,
        enemy.sprite,
        () => this.fireballHitEnemy(fireball, enemy),
        undefined,
        this
      );
    }
  }

  private fireballHitEnemy(fireball: Phaser.GameObjects.Arc, enemy: EnemyInfo): void {
    if (!enemy.alive) return;

    // Destroy enemy with squish effect
    enemy.alive = false;
    this.playSound('enemy-squish');

    this.tweens.add({
      targets: enemy.sprite,
      scaleY: 0,
      alpha: 0,
      duration: 200,
      onComplete: () => {
        enemy.sprite.destroy();
      },
    });

    // Destroy fireball
    this.destroyFireball(fireball);
  }

  private updateFireballs(): void {
    if (!this.player) return;

    // Remove fireballs that are too far from player (> 800px)
    for (let i = this.fireballProjectiles.length - 1; i >= 0; i--) {
      const fb = this.fireballProjectiles[i];
      if (!fb || !fb.active) {
        this.fireballProjectiles.splice(i, 1);
        continue;
      }
      const dist = Math.abs(fb.x - this.player.x);
      if (dist > 800) {
        this.destroyFireball(fb);
        this.fireballProjectiles.splice(i, 1);
      }
    }
  }

  private updateFireballIndicator(): void {
    // Keep the fireball indicator near the player
    if (this.fireballIndicator && this.player && this.hasFireball) {
      this.fireballIndicator.setPosition(this.player.x + 30, this.player.y - 50);
    }
    // Toggle touch fireball button visibility
    const fireBtn = (this as any)._touchFireBtn as Phaser.GameObjects.Rectangle | undefined;
    const fireLabel = (this as any)._touchFireLabel as Phaser.GameObjects.Text | undefined;
    if (fireBtn && fireLabel) {
      fireBtn.setVisible(this.hasFireball);
      fireLabel.setVisible(this.hasFireball);
    }
  }

  private destroyFireball(fireball: Phaser.GameObjects.Arc): void {
    if (fireball && fireball.active) {
      fireball.destroy();
    }
  }

  // ─── Feature: Shield Effect ───────────────────────────────────────────────

  private activateShield(): void {
    this.hasShield = true;

    // Show indicator
    if (this.player) {
      const indicator = this.add.text(this.player.x, this.player.y - 80, '🛡️ Escudo!', {
        fontSize: '16px', color: '#4488ff', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(LAYER_DEPTHS.HUD);
      this.tweens.add({
        targets: indicator,
        y: indicator.y - 30,
        alpha: 0,
        duration: 1500,
        onComplete: () => indicator.destroy(),
      });

      // Blue tint on player
      this.player.setTint(0x88aaff);
    }

    // Cancel previous timer if any
    if (this.shieldTimer) {
      this.shieldTimer.destroy();
    }

    // Shield lasts max 20 seconds
    this.shieldTimer = this.time.delayedCall(20000, () => {
      this.hasShield = false;
      if (this.player) {
        this.player.clearTint();
      }
      this.playSound('powerup-expire');
    });
  }

  // ─── Feature 6: Progress Persistence ─────────────────────────────────────

  private getProgressKey(): string {
    switch (this.mapId) {
      case 'montanha-gelada':
        return 'awe_progress_montanha-gelada';
      case 'reino-encantado':
        return 'awe_progress_reino-encantado';
      case 'espaco-sideral':
        return 'awe_progress_espaco-sideral';
      default:
        return PROGRESS_KEY;
    }
  }

  private saveProgress(): void {
    const completedCaves = this.triggers
      .filter((t) => t.completed)
      .map((t) => t.caveId);

    try {
      localStorage.setItem(this.getProgressKey(), JSON.stringify({ completedCaves }));
    } catch (e) {
      console.warn('[GameScene] Failed to save progress:', e);
    }
  }

  private restoreProgress(): void {
    try {
      const saved = localStorage.getItem(this.getProgressKey());
      if (!saved) return;

      const data = JSON.parse(saved);
      const completedCaves: string[] = data.completedCaves || [];

      for (const caveId of completedCaves) {
        const trigger = this.triggers.find((t) => t.caveId === caveId);
        if (trigger) {
          trigger.completed = true;
          trigger.sprite.setAlpha(0.4);
          trigger.promptText?.setVisible(false);
          this.tweens.killTweensOf(trigger.sprite);
        }
      }

      // Check if all caves were already complete
      this.checkAllCavesComplete();
    } catch (e) {
      console.warn('[GameScene] Failed to restore progress:', e);
    }
  }

  private checkAllCavesComplete(): void {
    const allDone = this.triggers.length > 0 && this.triggers.every((t) => t.completed);
    if (allDone && this.finishTriggerSystem && !this.finishTriggerSystem.isActive) {
      this.finishTriggerSystem.activate();
      this.showPortal();
    }
  }

  private showMapComplete(): void {
    const { width, height } = this.scale;

    // Full-screen overlay
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.75);
    overlay.setScrollFactor(0);
    overlay.setDepth(LAYER_DEPTHS.HUD + 10);

    // Congratulations text per world
    const mapTitles: Record<string, { title: string; subtitle: string; btnLabel: string }> = {
      'floresta-encantada': {
        title: '🎉 Floresta Completa! 🎉',
        subtitle: 'Desbloqueaste a Montanha Gelada!',
        btnLabel: '➡️ Ir para Montanha Gelada',
      },
      'montanha-gelada': {
        title: '🏔️ Montanha Gelada Completa! 🏔️',
        subtitle: 'Desbloqueaste o Reino Encantado!',
        btnLabel: '➡️ Ir para Reino Encantado',
      },
      'reino-encantado': {
        title: '🏰 Reino Encantado Completo! 🏰',
        subtitle: 'Desbloqueaste o Espaço Sideral!',
        btnLabel: '➡️ Ir para Espaço Sideral',
      },
      'espaco-sideral': {
        title: '🚀 Espaço Sideral Completo! 🚀',
        subtitle: 'Parabéns! Completaste todos os mundos!',
        btnLabel: '🗺️ Voltar ao Mapa',
      },
    };

    const info = mapTitles[this.mapId] ?? mapTitles['floresta-encantada'];

    const titleText = this.add.text(width / 2, height / 2 - 60, info.title, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '42px',
      color: '#ffd700',
      fontStyle: 'bold',
      align: 'center',
      stroke: '#000000',
      strokeThickness: 4,
    });
    titleText.setOrigin(0.5);
    titleText.setScrollFactor(0);
    titleText.setDepth(LAYER_DEPTHS.HUD + 11);

    const subtitleText = this.add.text(width / 2, height / 2, info.subtitle, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '24px',
      color: '#ffffff',
      align: 'center',
    });
    subtitleText.setOrigin(0.5);
    subtitleText.setScrollFactor(0);
    subtitleText.setDepth(LAYER_DEPTHS.HUD + 11);

    // Button: go to map select (where the new map is now unlocked)
    const btnText = this.add.text(width / 2, height / 2 + 70, info.btnLabel, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '26px',
      color: '#ffffff',
      backgroundColor: '#38B000',
      padding: { x: 24, y: 12 },
    });
    btnText.setOrigin(0.5);
    btnText.setScrollFactor(0);
    btnText.setDepth(LAYER_DEPTHS.HUD + 11);
    btnText.setInteractive({ useHandCursor: true });

    btnText.on('pointerdown', () => {
      this.scene.start('MapSelectScene');
    });

    // Pulse animation on button
    this.tweens.add({
      targets: btnText,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Block game input
    this.overlayActive = true;
  }

  /**
   * Adds a "Voltar ao Mundo" button on top of the overlay.
   * This is a safety mechanism in case the motor's completion detection fails.
   */
  private addOverlayReturnButton(caveId: string): void {
    if (!this.overlayBridge?.overlayElement) return;

    // Don't add if already exists
    if (document.querySelector('#awe-return-btn')) return;

    const btn = document.createElement('button');
    btn.id = 'awe-return-btn';
    btn.textContent = '✓ Voltar ao Mundo';
    btn.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 2000;
      padding: 16px 32px;
      font-size: 20px;
      font-weight: bold;
      background: #38B000;
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      touch-action: manipulation;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;

    btn.addEventListener('click', () => {
      btn.remove();
      // Mark cave as complete and return to overworld
      const trigger = this.triggers.find((t) => t.caveId === caveId);
      if (trigger) {
        trigger.completed = true;
        trigger.sprite.setAlpha(0.4);
        trigger.promptText?.setVisible(false);
        this.tweens.killTweensOf(trigger.sprite);
      }
      this.overlayBridge?.stopMotor();
      this.overlayBridge?.hide();
      this.overlayActive = false;
      this.saveProgress();
      this.checkAllCavesComplete();
    });

    document.body.appendChild(btn);
  }

  // ─── Level Complete ──────────────────────────────────────────────────────

  private showLevelComplete(): void {
    const { width, height } = this.scale;
    const camera = this.cameras.main;

    const overlay = this.add.rectangle(
      camera.scrollX + width / 2, height / 2,
      width, height, 0x000000, 0.7
    );
    overlay.setDepth(LAYER_DEPTHS.HUD + 10);
    overlay.setScrollFactor(0);

    const text = this.add.text(
      camera.scrollX + width / 2, height / 2 - 40,
      '🎉 Parabéns! 🎉\nCompletaste o nível!',
      {
        fontFamily: 'Arial, sans-serif',
        fontSize: '48px',
        color: '#ffd700',
        fontStyle: 'bold',
        align: 'center',
        stroke: '#000000',
        strokeThickness: 4,
      }
    );
    text.setOrigin(0.5);
    text.setDepth(LAYER_DEPTHS.HUD + 11);
    text.setScrollFactor(0);

    const btnText = this.add.text(
      camera.scrollX + width / 2, height / 2 + 60,
      'Voltar ao Mapa',
      {
        fontFamily: 'Arial, sans-serif',
        fontSize: '28px',
        color: '#ffffff',
        backgroundColor: '#4caf50',
        padding: { x: 20, y: 10 },
      }
    );
    btnText.setOrigin(0.5);
    btnText.setDepth(LAYER_DEPTHS.HUD + 11);
    btnText.setScrollFactor(0);
    btnText.setInteractive({ useHandCursor: true });
    btnText.on('pointerdown', () => {
      this.scene.start('MapSelectScene');
    });
  }

  // ─── Orientation & Viewport ──────────────────────────────────────────────

  private setupOrientationListener(): void {
    this.resizeHandler = () => {
      if (this.orientationTimer) {
        this.orientationTimer.destroy();
      }
      this.orientationTimer = this.time.delayedCall(50, () => {
        this.handleOrientationChange();
      });
    };
    this.scale.on('resize', this.resizeHandler);
  }

  private handleOrientationChange(): void {
    const camW = this.cameras.main.width;
    const camH = this.cameras.main.height;
    const newOrientation = camW >= camH ? 'landscape' : 'portrait';

    if (newOrientation !== this.currentOrientation) {
      this.currentOrientation = newOrientation;
    }

    for (const layer of this.parallaxLayers) {
      layer.image.setPosition(camW / 2, camH / 2);
      layer.image.setDisplaySize(camW, camH);
    }

    this.applyViewportSafeArea();
  }

  private applyViewportSafeArea(): void {
    const camera = this.cameras.main;
    // On mobile, account for safe area insets (notch, home indicator)
    // The Phaser FIT scale mode handles most of this, but we ensure camera viewport is correct
    camera.setViewport(0, 0, this.scale.width, this.scale.height);

    // Offset touch buttons from safe areas on iOS
    if (this.sys.game.device.os.iOS && this.touchLeftBtn) {
      const safeMargin = 34; // iOS home indicator height
      const currentY = BASE_HEIGHT - 16 - 60; // margin + btnSize/2
      this.touchLeftBtn.setY(currentY - safeMargin);
      this.touchRightBtn?.setY(currentY - safeMargin);
      this.touchJumpBtn?.setY(currentY - safeMargin);
      this.touchEBtn?.setY(currentY - safeMargin);
    }
  }

  // ─── Task 11: Map Entry Animation ──────────────────────────────────────────

  private playMapEntryAnimation(): void {
    const { width, height } = this.scale;
    const camera = this.cameras.main;

    // Fade in from black
    camera.fadeIn(500, 0, 0, 0);

    // Map name display (text only, no icons — Requirement 4.1)
    const mapNames: Record<string, string> = {
      'floresta-encantada': 'Floresta Encantada',
      'montanha-gelada': 'Montanha Gelada',
      'reino-encantado': 'Reino Encantado',
      'espaco-sideral': 'Espaço Sideral',
    };
    const displayName = mapNames[this.mapId] ?? this.mapId;

    const nameText = this.add.text(width / 2, height * 0.35, displayName, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '44px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      align: 'center',
    });
    nameText.setOrigin(0.5);
    nameText.setScrollFactor(0);
    nameText.setDepth(LAYER_DEPTHS.HUD + 20);
    nameText.setAlpha(0);

    // Animate name in and out
    this.tweens.add({
      targets: nameText,
      alpha: 1,
      y: height * 0.3,
      duration: 600,
      ease: 'Power2',
      onComplete: () => {
        this.tweens.add({
          targets: nameText,
          alpha: 0,
          y: height * 0.25,
          delay: 1500,
          duration: 500,
          onComplete: () => nameText.destroy(),
        });
      },
    });
  }

  // ─── Task 12: Particles on Power-Up Collect ───────────────────────────────

  private emitCollectParticles(x: number, y: number, color: number): void {
    // Create burst of 12 small circles that fly outward and fade
    for (let i = 0; i < 12; i++) {
      const angle = (Math.PI * 2 * i) / 12;
      const speed = 80 + Math.random() * 60;
      const particle = this.add.circle(x, y, 4 + Math.random() * 3, color, 1);
      particle.setDepth(LAYER_DEPTHS.HUD);

      this.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * speed,
        y: y + Math.sin(angle) * speed,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: 400 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      });
    }
  }

  // ─── Task 13: Tutorial Overlay (first time only) ──────────────────────────

  private showTutorialIfFirstTime(): void {
    const tutorialKey = 'awe_tutorial_shown';
    try {
      if (localStorage.getItem(tutorialKey)) return;
      localStorage.setItem(tutorialKey, '1');
    } catch {
      return; // Can't access localStorage, skip tutorial
    }

    // Block input during tutorial
    this.overlayActive = true;

    const { width, height } = this.scale;

    // Semi-transparent overlay
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.7);
    overlay.setScrollFactor(0);
    overlay.setDepth(LAYER_DEPTHS.HUD + 30);

    // Tutorial tips
    const tips = [
      '← →  Mover',
      '↑ ou Espaço  Saltar',
      'E  Entrar na caverna',
      'F  Bola de fogo (após recolher 🔥)',
    ];

    const title = this.add.text(width / 2, height * 0.2, 'Como Jogar', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '36px',
      color: '#ffd700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5).setScrollFactor(0).setDepth(LAYER_DEPTHS.HUD + 31);

    const tipTexts: Phaser.GameObjects.Text[] = [];
    tips.forEach((tip, i) => {
      const t = this.add.text(width / 2, height * 0.35 + i * 50, tip, {
        fontFamily: 'Arial, sans-serif',
        fontSize: '22px',
        color: '#ffffff',
        align: 'center',
      }).setOrigin(0.5).setScrollFactor(0).setDepth(LAYER_DEPTHS.HUD + 31);
      tipTexts.push(t);
    });

    // "Jogar!" button
    const btnBg = this.add.graphics().setScrollFactor(0).setDepth(LAYER_DEPTHS.HUD + 31);
    const btnW = 200, btnH = 56, btnX = width / 2, btnY = height * 0.78;
    btnBg.fillStyle(0x38B000, 1);
    btnBg.fillRoundedRect(btnX - btnW / 2, btnY - btnH / 2, btnW, btnH, 14);

    const btnText = this.add.text(btnX, btnY, 'Jogar!', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(LAYER_DEPTHS.HUD + 32);

    const btnHit = this.add.rectangle(btnX, btnY, btnW, btnH, 0xffffff, 0)
      .setInteractive({ useHandCursor: true })
      .setScrollFactor(0)
      .setDepth(LAYER_DEPTHS.HUD + 32);

    btnHit.on('pointerdown', () => {
      // Clean up tutorial
      overlay.destroy();
      title.destroy();
      tipTexts.forEach((t) => t.destroy());
      btnBg.destroy();
      btnText.destroy();
      btnHit.destroy();
      this.overlayActive = false;
    });
  }

  // ─── Feature: Collectibles & Finish Trigger ────────────────────────────────

  /**
   * Sets up the CollectibleSystem, spawns collectible entities at configured
   * positions, and wires power-up trigger events (vowels, numbers, alphabet).
   *
   * Requirements: 3.1, 7.3, 7.4, 8.1, 9.1, 10.1
   */
  private setupCollectibles(): void {
    // Instantiate system
    this.collectibleSystem = new CollectibleSystem();
    this.collectibleEntities = [];

    // Restore any previously collected items
    this.restoreCollectibleProgress();

    // Load placements for current world
    const placements = getCollectiblePlacements(this.mapId);

    // Spawn entities at configured positions
    for (const placement of placements) {
      // Determine type and value from the ID
      const type = placement.id.startsWith('letter-') ? 'letter' : 'number';
      const value = placement.id.startsWith('letter-')
        ? placement.id.slice(7)   // "letter-A" → "A"
        : placement.id.slice(7);  // "number-3" → "3"

      // Skip if already collected (from restored progress)
      if (this.collectibleSystem.letters.get(value) === true && type === 'letter') continue;
      if (this.collectibleSystem.numbers.get(value) === true && type === 'number') continue;

      const config: CollectibleConfig = {
        id: placement.id,
        type: type as 'letter' | 'number',
        value,
        spriteKey: placement.id, // e.g. "letter-A", "number-3"
        x: placement.x,
        y: placement.y,
      };

      const collectible = new Collectible(this, config);
      this.collectibleEntities.push(collectible);
    }

    // Listen for collectible-collected events from entities
    this.events.on('collectible-collected', (data: { id: string; type: string; value: string }) => {
      if (this.collectibleSystem) {
        this.collectibleSystem.collect(data.id);
        this.saveCollectibleProgress();
      }
    });

    // Listen for power-up trigger events from CollectibleSystem
    this.collectibleSystem.on('vowels-complete', () => {
      this.activateFireballWithDuration(10000); // 10s
    });

    this.collectibleSystem.on('numbers-complete', () => {
      this.activateFireballWithDuration(10000); // 10s
    });

    this.collectibleSystem.on('alphabet-complete', () => {
      this.activateFireballWithDuration(Infinity); // Permanent
    });
  }

  /**
   * Sets up the CollectiblesHUD and wires it to CollectibleSystem events.
   * Creates the HUD instance, listens for 'collected' and power-up completion events,
   * and provides countdown timer display when a 10s fireball is active.
   *
   * Requirements: 8.2, 9.2, 11.1, 11.5
   */
  private setupCollectiblesHUD(): void {
    if (!this.collectibleSystem) return;

    this.collectiblesHUD = new CollectiblesHUD();
    this.collectiblesHUD.create(this);

    // Wire 'collected' event → animate the corresponding HUD cell
    this.collectibleSystem.on('collected', (data) => {
      if (data.item && this.collectiblesHUD) {
        this.collectiblesHUD.animateCollection(data.item.id);
      }
    });

    // Wire power-up completion events → show celebration in HUD
    this.collectibleSystem.on('vowels-complete', () => {
      if (this.collectiblesHUD) {
        this.collectiblesHUD.showPowerUpCelebration('vowels');
      }
    });

    this.collectibleSystem.on('numbers-complete', () => {
      if (this.collectiblesHUD) {
        this.collectiblesHUD.showPowerUpCelebration('numbers');
      }
    });

    this.collectibleSystem.on('alphabet-complete', () => {
      if (this.collectiblesHUD) {
        this.collectiblesHUD.showPowerUpCelebration('alphabet');
      }
    });
  }

  /**
   * Updates the CollectiblesHUD each frame with current CollectibleSystem state.
   * Also updates the fireball countdown timer display when active.
   *
   * Requirements: 11.1, 8.2, 9.2
   */
  private updateCollectiblesHUD(): void {
    if (this.collectiblesHUD && this.collectibleSystem) {
      this.collectiblesHUD.update(this.collectibleSystem);
    }

    // Update fireball countdown timer
    this.updateFireballCountdown();
  }

  /**
   * Updates the countdown timer display for timed fireballs (10s from vowels/numbers).
   * Shows remaining seconds in the HUD. Destroys itself when expired.
   */
  private updateFireballCountdown(): void {
    if (!this.fireballCountdownText) return;

    const now = this.time.now;
    const remaining = Math.max(0, Math.ceil((this.fireballCountdownEndTime - now) / 1000));

    if (remaining <= 0) {
      this.fireballCountdownText.destroy();
      this.fireballCountdownText = null;
      return;
    }

    this.fireballCountdownText.setText(`🔥 ${remaining}s`);
  }

  /**
   * Sets up the FinishTriggerSystem.
   * The trigger activates only when all 5 caves are complete.
   *
   * Requirements: 3.1, 3.6
   */
  private setupFinishTrigger(): void {
    this.finishTriggerSystem = new FinishTriggerSystem();
    this.finishTriggerReached = false;

    // If all caves are already complete (restored from progress), activate immediately
    const allDone = this.triggers.length > 0 && this.triggers.every((t) => t.completed);
    if (allDone) {
      this.finishTriggerSystem.activate();
      this.showPortal();
    }
  }

  /**
   * Updates collectible entities (float animation) and checks collision with player.
   *
   * Requirements: 7.3, 7.4
   */
  private updateCollectibles(delta: number): void {
    if (!this.player || this.overlayActive) return;

    const playerBounds = this.player.getBounds();

    for (let i = this.collectibleEntities.length - 1; i >= 0; i--) {
      const entity = this.collectibleEntities[i];
      if (entity.isCollected) {
        this.collectibleEntities.splice(i, 1);
        continue;
      }

      // Update float animation
      entity.update(delta);

      // Check overlap with player (simple AABB)
      const entityX = entity.x;
      const entityY = entity.y;
      const halfW = 20; // BODY_WIDTH / 2
      const halfH = 20; // BODY_HEIGHT / 2

      const entityLeft = entityX - halfW;
      const entityRight = entityX + halfW;
      const entityTop = entityY - halfH;
      const entityBottom = entityY + halfH;

      const playerLeft = playerBounds.x;
      const playerRight = playerBounds.x + playerBounds.width;
      const playerTop = playerBounds.y;
      const playerBottom = playerBounds.y + playerBounds.height;

      const overlaps =
        playerRight > entityLeft &&
        playerLeft < entityRight &&
        playerBottom > entityTop &&
        playerTop < entityBottom;

      if (overlaps) {
        entity.collect(); // Triggers animation, sound, and emits 'collectible-collected'
      }
    }
  }

  /**
   * Checks if player collides with the finish trigger zone.
   * On collision: emits 'finish-trigger-reached' event and transitions to WorldCompleteScene.
   *
   * Requirements: 3.2, 3.3
   */
  private updateFinishTrigger(): void {
    if (!this.player || !this.finishTriggerSystem || this.finishTriggerReached || this.overlayActive) return;
    if (!this.finishTriggerSystem.isActive) return;

    const playerBounds = this.player.getBounds();
    const collision = this.finishTriggerSystem.checkCollision({
      x: playerBounds.x,
      y: playerBounds.y,
      width: playerBounds.width,
      height: playerBounds.height,
    });

    if (collision) {
      this.finishTriggerReached = true;

      // Emit event for external listeners
      this.events.emit('finish-trigger-reached', { mapId: this.mapId });

      // Play completion sound
      this.playSound('powerup-collect');

      // Determine victory image key
      const completionImageKey = `${this.mapId}-vitoria`;

      // Transition to WorldCompleteScene
      this.scene.start('WorldCompleteScene', {
        mapId: this.mapId,
        completionImageKey,
      } as import('./WorldCompleteScene').WorldCompleteSceneData);
    }
  }

  /**
   * Shows the portal sprite at the finish trigger position with a pulse animation.
   *
   * Requirement: 3.6
   */
  private showPortal(): void {
    if (this.portalSprite) return; // Already shown

    const pos = this.finishTriggerSystem?.position;
    if (!pos) return;

    const portalKey = 'portal-fim';
    if (this.textures.exists(portalKey)) {
      this.portalSprite = this.add.image(pos.x, pos.y - 64, portalKey);
      this.portalSprite.setDisplaySize(260, 260);
      this.portalSprite.setDepth(LAYER_DEPTHS.TRIGGERS);
    } else {
      // Fallback: create a glowing circle as portal indicator
      const gfx = this.add.graphics();
      gfx.fillStyle(0x7744ff, 0.8);
      gfx.fillCircle(0, 0, 48);
      gfx.setPosition(pos.x, pos.y - 64);
      gfx.setDepth(LAYER_DEPTHS.TRIGGERS);

      const label = this.add.text(pos.x, pos.y - 100, '🌀 Portal', {
        fontSize: '20px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      }).setOrigin(0.5).setDepth(LAYER_DEPTHS.TRIGGERS + 1);

      // Use the graphics as "portal sprite" for the pulse animation
      this.portalSprite = this.add.image(pos.x, pos.y - 64, '__WHITE');
      this.portalSprite.setDisplaySize(96, 96);
      this.portalSprite.setAlpha(0); // invisible — just a target for tween reference

      // Pulse animation on the graphics
      this.tweens.add({
        targets: gfx,
        scaleX: 1.2,
        scaleY: 1.2,
        alpha: 0.6,
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      this.tweens.add({
        targets: label,
        y: label.y - 6,
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      return;
    }

    // Add a label above portal
    const portalLabel = this.add.text(pos.x, pos.y - 130, '🌀 Portal!', {
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(LAYER_DEPTHS.TRIGGERS + 1);

    this.tweens.add({
      targets: portalLabel,
      y: portalLabel.y - 6,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Activates the fireball power-up with a specified duration.
   * Overrides any existing timed fireball. Infinity = permanent.
   *
   * Requirements: 8.1, 9.1, 10.1
   */
  private activateFireballWithDuration(durationMs: number): void {
    this.hasFireball = true;
    this.fireballCooldown = 0;

    // Show indicator
    if (this.player) {
      const label = durationMs === Infinity ? '🔥 Fogo Permanente!' : '🔥 Bola de Fogo! [F]';
      const indicator = this.add.text(this.player.x, this.player.y - 80, label, {
        fontSize: '16px', color: '#ff6600', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(LAYER_DEPTHS.HUD);
      this.tweens.add({
        targets: indicator,
        y: indicator.y - 30,
        alpha: 0,
        duration: 1500,
        onComplete: () => indicator.destroy(),
      });

      // Persistent indicator
      if (this.fireballIndicator) {
        this.fireballIndicator.destroy();
      }
      this.fireballIndicator = this.add.text(0, 0, '🔥', {
        fontSize: '20px',
      }).setDepth(LAYER_DEPTHS.HUD);
    }

    // Cancel previous timer
    if (this.fireballTimer) {
      this.fireballTimer.destroy();
      this.fireballTimer = null;
    }

    // Set up countdown timer display for timed fireballs
    if (durationMs !== Infinity) {
      // Show countdown text in the HUD area (top-left, below the cave HUD)
      if (this.fireballCountdownText) {
        this.fireballCountdownText.destroy();
      }
      this.fireballCountdownEndTime = this.time.now + durationMs;
      this.fireballCountdownText = this.add.text(20, 50, `🔥 ${Math.ceil(durationMs / 1000)}s`, {
        fontFamily: 'Arial, sans-serif',
        fontSize: '18px',
        color: '#ff6600',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      });
      this.fireballCountdownText.setScrollFactor(0);
      this.fireballCountdownText.setDepth(LAYER_DEPTHS.HUD);
    } else {
      // Permanent fireball — remove countdown if it exists
      if (this.fireballCountdownText) {
        this.fireballCountdownText.destroy();
        this.fireballCountdownText = null;
      }
    }

    // Set up expiration (unless permanent)
    if (durationMs !== Infinity) {
      this.fireballTimer = this.time.delayedCall(durationMs, () => {
        this.hasFireball = false;
        if (this.fireballIndicator) {
          this.fireballIndicator.destroy();
          this.fireballIndicator = null;
        }
        // Countdown text will be cleaned up by updateFireballCountdown()
        this.playSound('powerup-expire');
      });
    }
    // If Infinity: no timer, fireball stays active for the rest of the level
  }

  // ─── Collectible Persistence ──────────────────────────────────────────────

  /**
   * Saves collectible progress to localStorage.
   */
  private saveCollectibleProgress(): void {
    if (!this.collectibleSystem) return;
    try {
      const key = `awe_collectibles_${this.mapId}`;
      const data = this.collectibleSystem.serialize();
      localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.warn('[GameScene] Failed to save collectible progress:', e);
    }
  }

  /**
   * Restores collectible progress from localStorage.
   */
  private restoreCollectibleProgress(): void {
    if (!this.collectibleSystem) return;
    try {
      const key = `awe_collectibles_${this.mapId}`;
      const stored = localStorage.getItem(key);
      if (!stored) return;
      const data = JSON.parse(stored);
      if (data && data.collectedLetters && data.collectedNumbers) {
        this.collectibleSystem.deserialize(data);
      }
    } catch (e) {
      console.warn('[GameScene] Failed to restore collectible progress:', e);
    }
  }

  // ─── Cleanup ─────────────────────────────────────────────────────────────

  shutdown(): void {
    if (this.resizeHandler) {
      this.scale.off('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.overlayBridge) {
      this.overlayBridge.destroy();
      this.overlayBridge = null;
    }

    if (this.orientationTimer) {
      this.orientationTimer.destroy();
      this.orientationTimer = null;
    }

    if (this.invincibilityTimer) {
      this.invincibilityTimer.destroy();
      this.invincibilityTimer = null;
    }

    if (this.fireballTimer) {
      this.fireballTimer.destroy();
      this.fireballTimer = null;
    }

    if (this.shieldTimer) {
      this.shieldTimer.destroy();
      this.shieldTimer = null;
    }

    // Cleanup fireball projectiles
    for (const fb of this.fireballProjectiles) {
      if (fb && fb.active) fb.destroy();
    }
    this.fireballProjectiles = [];

    if (this.fireballIndicator) {
      this.fireballIndicator.destroy();
      this.fireballIndicator = null;
    }

    // Cleanup collectible system
    if (this.collectiblesHUD) {
      this.collectiblesHUD.destroy();
      this.collectiblesHUD = null;
    }
    if (this.fireballCountdownText) {
      this.fireballCountdownText.destroy();
      this.fireballCountdownText = null;
    }
    this.collectibleSystem = null;
    this.finishTriggerSystem = null;
    this.collectibleEntities = [];
    this.portalSprite = null;
    this.finishTriggerReached = false;
  }
}
